import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useMachine } from '@xstate/react';
import InputFieldComponent from '@components/web-landingpage/InputField';
import CheckboxComponent from '../Checkbox';
import { ErrorToastComponent } from '../ErrorToast';
import GoogleIconComponent from '@components/icons/googleIcon';
import registrationStateMachine from '../../../machines/registrationStateMachine';
import {
  ButtonText,
  FormCheckboxText,
  FormErrorText,
} from '@components/web-landingpage/Texts';
import { IconButtonComponent } from '@components/buttons/IconButton';
import PrimaryButtonComponent from '@components/buttons/PrimaryButton';
import { medium_xs } from '../texts/Styles';
import { LoginOrSignupLocation } from '@customtypes/events';

function RegistrationFormComponent({
  trackLoginOrSignupLocation,
}: {
  trackLoginOrSignupLocation: LoginOrSignupLocation;
}) {
  const [registrationState, sendRegistrationStateEvent] = useMachine(
    registrationStateMachine,
    {
      input: {
        trackLoginOrSignupLocation,
      },
    },
  );

  const {
    email: emailError,
    password: passwordError,
    terms: termsError,
  } = registrationState.context.state.errors;

  const { email, password, checkedTerms } = registrationState.context.state;

  useEffect(() => {
    if (registrationState.matches('serviceError')) {
      toast.dismiss();
      toast.custom((toast) => (
        <ErrorToastComponent toast={toast}>
          {registrationState.context.state.errors.serviceError}
        </ErrorToastComponent>
      ));
    }
  }, [registrationState.context.state.errors]);

  return (
    <div className="space-y-8 tablet:space-y-6">
      <div className="space-y-4">
        <div className="text-text-primary-dark">
          <div className={`${medium_xs} mb-2 text-start`}>Email</div>
          <InputFieldComponent
            id="email"
            placeholder="Your e-mail"
            name="email"
            type="email"
            required={true}
            value={email}
            onChange={(e) => {
              sendRegistrationStateEvent({
                type: 'ENTER_EMAIL',
                email: e.target.value,
              });
            }}
            autoComplete="off"
            errorMessage={emailError}
            onEnter={() => {
              sendRegistrationStateEvent({
                type: 'SUBMIT_FORM',
                data: {
                  email,
                  password,
                  checkedTerms,
                },
              });
            }}
          />
          <div className={`${medium_xs} mb-2 text-start`}>Password</div>
          <InputFieldComponent
            id="password"
            placeholder="Your password"
            name="password"
            type="password"
            required={true}
            value={password}
            onChange={(e) => {
              sendRegistrationStateEvent({
                type: 'ENTER_PASSWORD',
                password: e.target.value,
              });
            }}
            autoComplete="off"
            errorMessage={passwordError}
            onEnter={() => {
              sendRegistrationStateEvent({
                type: 'SUBMIT_FORM',
                data: {
                  email,
                  password,
                  checkedTerms,
                },
              });
            }}
          />
          <CheckboxComponent
            errorMessage={termsError}
            name="registration"
            onChange={(e) => {
              sendRegistrationStateEvent({
                type: 'TOGGLE_TERMS',
                checkedTerms: e.target.checked,
              });
            }}
          >
            <FormCheckboxText>
              I agree to Mimo&apos;s{' '}
              <a
                href="https://mimo.org/terms"
                className="underline decoration-1"
              >
                Terms of Service
              </a>
              .
            </FormCheckboxText>
          </CheckboxComponent>
          <FormErrorText className="h-8 text-left text-marketing-form-error">
            {termsError}
          </FormErrorText>
          <PrimaryButtonComponent
            className="w-full"
            onClick={() => {
              sendRegistrationStateEvent({
                type: 'SUBMIT_FORM',
                data: {
                  email,
                  password,
                  checkedTerms,
                },
              });
            }}
          >
            <ButtonText>
              {registrationState.matches('loading')
                ? 'Creating your account ...'
                : 'Sign up and learn for free'}
            </ButtonText>
          </PrimaryButtonComponent>
        </div>
        <IconButtonComponent
          className="h-12"
          title="Sign in with Google"
          onClick={() => {
            sendRegistrationStateEvent({
              type: 'REGISTER_WITH_GOOGLE',
            });
          }}
        >
          <GoogleIconComponent className="h-6 w-6 flex-shrink-0" />
        </IconButtonComponent>
      </div>
    </div>
  );
}

export default RegistrationFormComponent;
